/**
 * The team's members: who is on it, with leavers first and marked, and — for
 * the team's manager or an admin — "Add member" and a confirmed "Remove".
 *
 * Adding uses the directory search, which only offers people who have not
 * left. A change is confirmed with a toast only once the server has read the
 * team back from Ivanti and seen it (19 Oct); otherwise the dialog shows the
 * error and the page reloads the team as Ivanti holds it. Removing unlinks the person from the team in Ivanti; their employee
 * record is untouched, which the confirmation says, because "remove" next to
 * a name reads like deleting the person.
 */

import { Icon, Trash2, UserPlus } from "@norican/icons";
import {
  AlertDialog,
  Badge,
  Button,
  DataGrid,
  type DataGridColumn,
  Dialog,
  Field,
  Typography,
  useToastManager,
} from "@norican/react";
import { useMemo, useState } from "react";

import { EmptyStateBlock, ErrorState } from "@/components/common";
import { EmployeePicker } from "@/features/tickets/components/employee-picker";
import { useT } from "@/i18n";
import type { DirectoryEntry, TeamDetailResponse, TeamMember } from "@/lib/api";

import { useAddTeamMember, useRemoveTeamMember } from "./api";

export interface TeamMembersSectionProps {
  team: TeamDetailResponse;
  canEdit: boolean;
}

function AddMemberDialog({
  team,
  open,
  onOpenChange,
}: {
  team: TeamDetailResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useT();
  const toast = useToastManager();
  const add = useAddTeamMember(team.id);
  const [employee, setEmployee] = useState<DirectoryEntry | null>(null);

  function close(next: boolean) {
    if (!next) {
      setEmployee(null);
      add.reset();
    }
    onOpenChange(next);
  }

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md" className="flex w-full flex-col">
          <Dialog.Header>
            <Dialog.Title>{t("teams.detail.addMemberTitle")}</Dialog.Title>
            <Dialog.CloseButton label={t("teams.detail.cancel")} />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4">
            {add.error == null ? null : <ErrorState error={add.error} variant="inline" />}
            <Field label={t("teams.detail.addMemberLabel")} hint={t("teams.detail.addMemberHint")}>
              <EmployeePicker
                value={employee?.id ?? null}
                onChange={setEmployee}
                aria-label={t("teams.detail.addMemberLabel")}
              />
            </Field>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Close render={<Button variant="outline" tone="neutral" type="button" />}>
              {t("teams.detail.cancel")}
            </Dialog.Close>
            <Button
              loading={add.isPending}
              disabled={employee === null}
              onClick={() => {
                if (employee !== null) {
                  add.mutate(employee.id, {
                    onSuccess: (fresh) => {
                      toast.add({
                        title: t("teams.detail.added", {
                          name: employee.display_name,
                          team: fresh.name,
                        }),
                        type: "success",
                      });
                      close(false);
                    },
                  });
                }
              }}
            >
              {t("teams.detail.add")}
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function TeamMembersSection({ team, canEdit }: TeamMembersSectionProps) {
  const { t } = useT();
  const toast = useToastManager();
  const remove = useRemoveTeamMember(team.id);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<TeamMember | null>(null);

  const members = team.members ?? [];

  const columns = useMemo<DataGridColumn<TeamMember>[]>(() => {
    const base: DataGridColumn<TeamMember>[] = [
      {
        id: "name",
        header: t("teams.column.name"),
        width: 220,
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-2">
            <span>{row.display_name}</span>
            {row.has_left ? (
              <Badge tone="danger" variant="subtle" size="sm">
                {t("teams.detail.left")}
              </Badge>
            ) : null}
          </span>
        ),
      },
      {
        id: "title",
        header: t("teams.column.title"),
        width: 180,
        accessor: (row) => row.job_title ?? "",
      },
      {
        id: "email",
        header: t("teams.column.email"),
        width: 240,
        cell: (row) => <span className="break-all">{row.email ?? ""}</span>,
      },
      {
        id: "status",
        header: t("teams.column.status"),
        width: 120,
        accessor: (row) => row.status ?? "",
      },
    ];
    if (!canEdit) {
      return base;
    }
    return [
      ...base,
      {
        id: "actions",
        header: <span className="sr-only">{t("teams.column.actions")}</span>,
        width: 120,
        align: "end",
        cell: (row) => (
          <Button
            variant="ghost"
            tone="danger"
            size="sm"
            startIcon={<Icon icon={Trash2} size="sm" />}
            onClick={() => {
              remove.reset();
              setRemoving(row);
            }}
          >
            {t("teams.detail.remove")}
          </Button>
        ),
      },
    ];
  }, [canEdit, remove, t]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Typography as="h2" variant="heading-4">
          {t("teams.detail.membersTitle")}
        </Typography>
        {canEdit ? (
          <Button
            size="sm"
            className="ml-auto"
            startIcon={<Icon icon={UserPlus} size="sm" />}
            onClick={() => setAdding(true)}
          >
            {t("teams.detail.addMember")}
          </Button>
        ) : null}
      </div>

      {members.length === 0 ? (
        <EmptyStateBlock title={t("teams.detail.noMembers")} description={null} size="sm" />
      ) : (
        <DataGrid
          aria-label={t("teams.detail.membersTitle")}
          columns={columns}
          data={members}
          getRowId={(row) => row.id}
          tableLayout="fixed"
        />
      )}

      {canEdit ? <AddMemberDialog team={team} open={adding} onOpenChange={setAdding} /> : null}

      <AlertDialog.Root
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(null);
          }
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Backdrop />
          <AlertDialog.Popup className="flex flex-col gap-4">
            <AlertDialog.Title>{t("teams.detail.removeTitle")}</AlertDialog.Title>
            <AlertDialog.Description>
              {t("teams.detail.removeBody", {
                name: removing?.display_name ?? "",
                team: team.name,
              })}
            </AlertDialog.Description>
            {remove.error == null ? null : <ErrorState error={remove.error} variant="inline" />}
            <div className="flex flex-wrap justify-end gap-2">
              <AlertDialog.Close render={<Button variant="outline" tone="neutral" />}>
                {t("teams.detail.cancel")}
              </AlertDialog.Close>
              <Button
                tone="danger"
                loading={remove.isPending}
                onClick={() => {
                  if (removing !== null) {
                    const name = removing.display_name;
                    remove.mutate(removing.id, {
                      onSuccess: (fresh) => {
                        toast.add({
                          title: t("teams.detail.removed", { name, team: fresh.name }),
                          type: "success",
                        });
                        setRemoving(null);
                      },
                    });
                  }
                }}
              >
                {t("teams.detail.removeConfirm")}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </section>
  );
}
